import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  commandsApi,
  paymentsApi,
  type DriverAccountQuery,
  type TransactionQuery,
} from '@/lib/api/endpoints';
import { queryKeys } from './keys';
import type {
  CreateDriverAccountInput,
  Id,
  PaginationQuery,
  RecordTransactionInput,
} from '@/types/api';

export function usePaymentSummary(enabled = true) {
  return useQuery({
    queryKey: queryKeys.payments.summary(),
    queryFn: () => paymentsApi.summary(),
    staleTime: 20_000,
    // The live map shows this to anyone, so callers without payment:read must
    // be able to skip the request rather than collect a 403.
    enabled,
  });
}

export function useDriverAccounts(query?: DriverAccountQuery) {
  return useQuery({
    queryKey: queryKeys.payments.accounts(query),
    queryFn: () => paymentsApi.accounts(query),
    placeholderData: (previous) => previous,
  });
}

export function useDriverAccount(id: Id | null | undefined) {
  return useQuery({
    queryKey: queryKeys.payments.account(id ?? ''),
    queryFn: () => paymentsApi.account(id as Id),
    enabled: Boolean(id),
  });
}

export function useTransactions(query?: TransactionQuery) {
  return useQuery({
    queryKey: queryKeys.payments.transactions(query),
    queryFn: () => paymentsApi.transactions(query),
    placeholderData: (previous) => previous,
  });
}

export function useCreateDriverAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDriverAccountInput) => paymentsApi.createAccount(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.payments.all });
      client.invalidateQueries({ queryKey: queryKeys.drivers.all });
      toast.success('Payments set up');
    },
  });
}

export function useUpdateDriverAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: Id; input: Partial<CreateDriverAccountInput> }) =>
      paymentsApi.updateAccount(id, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.payments.all });
      client.invalidateQueries({ queryKey: queryKeys.drivers.all });
      toast.success('Driver account updated');
    },
  });
}

/** Cash handed over at the yard. Gateway payments arrive by webhook instead. */
export function useRecordPayment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordTransactionInput) => paymentsApi.record(input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.payments.all });
      client.invalidateQueries({ queryKey: queryKeys.drivers.all });
      toast.success('Payment recorded');
    },
  });
}

/* ── immobilisation ───────────────────────────────────────────────────────── */

export function useVehicleCommands(vehicleId: Id | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.commands.list({ vehicleId: vehicleId ?? undefined, limit: 20 }),
    queryFn: () => commandsApi.list({ vehicleId: vehicleId ?? undefined, limit: 20 }),
    enabled: enabled && Boolean(vehicleId),
  });
}

/**
 * Both mutations invalidate vehicles as well as commands: immobilisation state
 * is denormalised onto the vehicle, so the map and the fleet panel need to
 * re-read it too.
 */
export function useImmobilise() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, reason }: { vehicleId: Id; reason: string }) =>
      commandsApi.immobilise(vehicleId, reason),
    onSuccess: (command) => {
      client.invalidateQueries({ queryKey: queryKeys.vehicles.all });
      client.invalidateQueries({ queryKey: queryKeys.commands.all });
      client.invalidateQueries({ queryKey: queryKeys.payments.all });
      client.invalidateQueries({ queryKey: queryKeys.drivers.all });
      toast.success(
        command.state === 'PENDING_SAFE_STOP'
          ? 'Immobilisation queued — it engages when the vehicle next stops'
          : 'Vehicle immobilised',
      );
    },
  });
}

export function useRelease() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, reason }: { vehicleId: Id; reason: string }) =>
      commandsApi.release(vehicleId, reason),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.vehicles.all });
      client.invalidateQueries({ queryKey: queryKeys.commands.all });
      client.invalidateQueries({ queryKey: queryKeys.payments.all });
      client.invalidateQueries({ queryKey: queryKeys.drivers.all });
      toast.success('Vehicle released');
    },
  });
}

export function useCommandHistory(query?: PaginationQuery) {
  return useQuery({
    queryKey: queryKeys.commands.list(query),
    queryFn: () => commandsApi.list(query),
  });
}
